import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { fmt } from "@/lib/api";

export default function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const { t } = useLang();

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className={`group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-[border-color,box-shadow,transform] duration-300 hover:border-[hsl(var(--primary))] hover:shadow-lg hover:-translate-y-1 ${product.span}`}
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={`/product/${product.slug}`} className="block">
        <div className="relative aspect-[4/5] overflow-hidden bg-slate-50">
          <img
            src={product.images?.[0]}
            alt={product.name}
            className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-110"
          />

          {product.stock > 0 && product.stock <= 8 && (
            <div className="absolute top-4 right-4 flex items-center gap-2 rounded-full border border-[hsl(var(--accent))] bg-white/90 px-3 py-1 backdrop-blur">
              <span className="h-2 w-2 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
              <span className="font-mono-x text-[10px] uppercase tracking-widest text-[hsl(var(--accent))]">
                {t("card.onlyLeft", { n: product.stock })}
              </span>
            </div>
          )}

          <div className="absolute top-4 left-4 flex flex-wrap gap-2">
            {product.tags?.map((tg) => (
              <span key={tg} className="font-mono-x text-[10px] uppercase tracking-widest text-white bg-[hsl(var(--primary))] rounded-full px-2.5 py-1">
                {tg}
              </span>
            ))}
          </div>
        </div>
      </Link>

      <div className="p-6 flex items-end justify-between gap-4">
        <div>
          <p className="font-mono-x text-[10px] uppercase tracking-widest text-slate-400 mb-1">{product.tagline}</p>
          <h3 className="font-display font-bold text-2xl leading-none text-slate-900">{product.name}</h3>
          <p className="mt-2 font-mono-x text-sm font-bold text-[hsl(var(--accent))]">{fmt(product.price)}</p>
        </div>
        <button
          data-testid={`quick-add-${product.slug}`}
          onClick={() => {
            addItem(product);
            toast.success(t("card.added", { name: product.name }));
          }}
          className="shrink-0 rounded-full bg-[hsl(var(--accent))] text-white font-mono-x text-xs uppercase tracking-widest px-5 py-3 transition-all duration-300 hover:glow-orange"
        >
          {t("card.add")}
        </button>
      </div>
    </motion.div>
  );
}
